 
import {useEffect, useState} from 'react';
import {setDoc} from 'firebase/firestore';
import {Accordion} from 'react-bootstrap';
import {
  AdvancedConsoleUnitTest,
  getAdvancedConsoleUnitTestReference,
  isAdvancedConsoleUnitTest,
} from '../../models/unitTests/advancedConsole';
import {uuidv4} from 'lib0/random';
import {type LoadedAdvancedConsoleUnitTestData} from './advancedConsoleUtils';
import {WithEditorData} from '../UnitTestSchema';
import {useDebounce} from 'use-debounce';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import {ExpandableCodeEditor} from 'components/editor/ExpandableCodeEditor';
import {useDocumentDataOnce} from 'react-firebase-hooks/firestore';
import {ASSIGNMENT_DATA_DEBOUNCE_MS} from '../constants';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';

const starterCodeInput = `
def grade_input(autograder_state):
    if autograder_state.get("problem_state") == "should_do_input":
      return SuccessfulInputTransition("Some Input")
    else:
      return FailedInputTransition("You prompted for input, but we didn't expect any!")

`;

const starterCodeOutput = `
import re

def grade_output(cur_line, autograder_state):
  # Initialize problem state
  if "problem_state" not in autograder_state:
    autograder_state["problem_state"] = "start"

  if autograder_state["problem_state"] == "start":
    # Check lines of output and extract variables
    m = re.match(r"This is the line we expect to see. We can also capture variables ([0-9]+)!", cur_line)

    if m is None:
      # This will inform the diff and error message shown to students
      return FailedTransition(cur_line, "output we expected", f"We expected [something] but instead got {cur_line}")
    
    # Check for other errors
    if len(m.groups()) < 1:
      return FailedTransition(cur_line, "example with one variable", f"We expected at least one number in your output, but found none in {cur_line})

    # Add captured variable to autograder_state for later use
    autograder_state["my_var"] = int(m.groups()[0])

    # Can register variables for random distribution checking
    register_random(autograder_state["my_var"], "var1", "uniform", {"min": 10, "max": 99, "tolerance": 0.0001})

    # Finally, success!
    autograder_state["problem_state"] = "next_state"
    return SuccessfulTransition()
  
  elif autograder_state["problem_state"] == "should_do_input":
    # Throw a special error for if we expected input but got output instead
    return FailedTransitionWithInput(cur_line, "placeholder input to display in diff", "We expected to supply input here, but you tried to print something instead!")

  else:
    # We are in an unknown state -- not sure how this would happen
    return FailedTransition(cur_line, "", f"An unknown error occured while grading your program. Check the Expected vs Observed output to see what went wrong.")
`;

const starterCodeFinish = `
def grade_finish(autograder_state):
    # Use this function to verify any state that should be true at the end of a run
    # This helps prevent issues like empty solutions passing

    # Here's an example!
    if autograder_state.get("problem_state") != "finished" or autograder_state["counter"] < 5:
        return FailedFinishState("Your program did not finish execution!")
    return SuccessfulFinishState()
`;

export const AdvancedConsoleEditor = ({
  loadedData,
  unitTestLoaderParameters,
  isEditable,
}: WithEditorData<LoadedAdvancedConsoleUnitTestData>) => {
  const {unitTests: serverUnitTests} = loadedData ?? {unitTests: []};
  const {courseId, assignmentId} = unitTestLoaderParameters;
  const t = useTranslateFunction('assignmentEditor');

  // A `branch` is basically one "test case" for the advanced autograder. Each branch has different grade_input/output scripts
  const serverBranches: AdvancedConsoleUnitTest[] = Array.isArray(
    serverUnitTests,
  )
    ? serverUnitTests.filter(isAdvancedConsoleUnitTest)
    : [];
  const [branches, setBranches] =
    useState<AdvancedConsoleUnitTest[]>(serverBranches);
  const [previouslyUploadedBranches, setPreviouslyUploadedBranches] =
    useState<AdvancedConsoleUnitTest[]>(serverBranches);
  function areBranchesEqual(
    branches1: AdvancedConsoleUnitTest[],
    branches2: AdvancedConsoleUnitTest[],
  ) {
    return (
      branches1.length === branches2.length &&
      branches1.every((branch, index) => {
        const branch2 = branches2[index];
        return (
          branch.key === branch2.key &&
          branch.name === branch2.name &&
          branch.gradingScripts?.onInput === branch2.gradingScripts?.onInput &&
          branch.gradingScripts?.onOutput ===
            branch2.gradingScripts?.onOutput &&
          branch.gradingScripts?.onFinish === branch2.gradingScripts?.onFinish
        );
      })
    );
  }
  const [debouncedBranches] = useDebounce(
    branches,
    ASSIGNMENT_DATA_DEBOUNCE_MS,
    {
      equalityFn: areBranchesEqual,
    },
  );
  const firebaseUnitTestDocumentReference = getAdvancedConsoleUnitTestReference(
    courseId,
    assignmentId,
  );
  const [
    advancedConsoleUnitTestDocument,
    advancedConsoleUnitTestDocumentLoading,
    advancedConsoleUnitTestDocumentError,
  ] = useDocumentDataOnce(firebaseUnitTestDocumentReference);

  const shouldSave =
    !advancedConsoleUnitTestDocumentLoading &&
    !advancedConsoleUnitTestDocumentError &&
    !areBranchesEqual(debouncedBranches, previouslyUploadedBranches);

  useEffect(() => {
    if (
      advancedConsoleUnitTestDocumentLoading ||
      advancedConsoleUnitTestDocumentError ||
      !advancedConsoleUnitTestDocument
    ) {
      return;
    }
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setBranches(advancedConsoleUnitTestDocument ?? []);
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setPreviouslyUploadedBranches(advancedConsoleUnitTestDocument ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [advancedConsoleUnitTestDocumentLoading]);
  useEffect(() => {
    if (!shouldSave) return;
    setDoc(firebaseUnitTestDocumentReference, debouncedBranches)
      .then(() => {
        setPreviouslyUploadedBranches(debouncedBranches);
      })
      .catch(error => {
        Swal.fire({
          title: 'Error saving advanced console unit tests',
          icon: 'error',
          toast: true,
          position: 'bottom',
          showConfirmButton: false,
        });
        console.error('Error saving advanced console unit tests: ', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [shouldSave]);

  const addAdvancedBranch = () => {
    setBranches([
      ...branches,
      {
        name: t('New Branch') as string,
        key: uuidv4(),
        gradingScripts: {
          onInput: starterCodeInput,
          onOutput: starterCodeOutput,
          onFinish: starterCodeFinish,
        },
      },
    ]);
  };

  const editAdvancedBranch = (i, newBranch) => {
    setBranches(branches.map((b, idx) => (idx === i ? newBranch : b)));
  };

  const deleteAdvancedBranch = i => {
    setBranches(branches.filter((b, idx) => idx !== i));
  };

  return (
    <div>
      <h4>{t('Advanced Autograder')}</h4>
      <div className="mt-4">
        <h5>{t('Custom Script Editor')}</h5>
        <p>
          {t('Each script is used to parse the input and output line by line.')}
        </p>
        <Accordion defaultActiveKey="0" className="mb-4">
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              {t('Random Autograder Documentation')}
            </Accordion.Header>
            <Accordion.Body>
              <div className="text-xl">
                <code>grade_input(autograder_state)</code>{' '}
                {t("runs every time the student's code calls")}{' '}
                <code>input</code>.{' '}
                {t("It's used to supply input dynamically.")}
                <br />
                <code>grade_output(cur_line, autograder_state)</code>{' '}
                {t('runs one for each line of output ')} (<code>cur_line</code>
                ).
                <br />
                <code>grade_finish(autograder_state)</code>{' '}
                {t(
                  "runs once the student's code has finished executing. Use it to prevent fencepost conditions and ensure the student's code printed all expected output.",
                )}
                <br />
                <br />
                {t('You have access to the following tools:')}
                <ul>
                  <li>
                    <code>autograder_state</code>{' '}
                    {t(
                      'is a dictionary to store any data you need to track during the autograder\'s execution. It persists across all <code>grade_output</code> and <code>grade_input</code> calls. Common uses are to store a "problem state" or store relevant variables captured from the output.',
                    )}
                  </li>
                  <li>
                    <code>
                      register_random(rv, var_name, dist=None, params=None,
                      error_msg="")
                    </code>
                    {t(
                      "adds a value to the list observations for a particular random variable we'd like the autograder to verify the distribution of.",
                    )}
                    <ul>
                      <li>
                        <code>rv</code> - {t('the value just observed.')}
                      </li>
                      <li>
                        <code>var_name</code> -{' '}
                        {t(
                          'a name we assign the variable to add more observations to it later.',
                        )}
                      </li>
                      <li>
                        <code>dist</code> -{' '}
                        {t('the type/distribution of the RV.')}
                        {t('Supported values are:')}{' '}
                        <code>"uniform", "geometric", "binomial"</code>
                      </li>
                      <li>
                        <code>params</code> -{' '}
                        {t('dict of parameters defining the distribution.')}
                        <br />
                        {t('Uniform:')}{' '}
                        <code>{`{"min": <${t('min value')}>, "max": <${t('max value')}>, "tolerance": <${t('max probability of false negative; Start with 0.0001, larger = faster test')}>`}</code>
                        <br />
                        {t('Geometric:')}{' '}
                        <code>{`{"p": <${t('probability defining the distribution')}>, "epsilon": <${t('guaranteed max distance between the predicted p and actual p; Start with 0.025, larger = faster test')}>`}</code>
                        <br />
                        {t('Binomial:')}{' '}
                        <code>{`{"p": <${t('probability defining the distribution')}>, "epsilon": <${t('guaranteed max distance between the predicted p and actual p; Start with 0.025, larger = faster test')}>`}</code>
                      </li>
                      <li>
                        <code>error_msg</code> -{' '}
                        {t(
                          'a message to display to the student if the distribution test fails.',
                        )}
                      </li>
                    </ul>
                    {t(
                      'All arguments must be specified on the first call for a given random variable ',
                    )}
                    (<code>var_name</code>).{' '}
                    {t('Subsequent calls require only the first two.')}
                  </li>
                </ul>
                <br />
                <br />
                {t('All functions must return a ')}
                <code>Transition</code> {t('object.')}
                <ul>
                  <li>
                    <code>SuccessfulTransition()</code> -{' '}
                    {t("indicates the student's code passed the test.")}
                  </li>
                  <li>
                    <code>
                      FailedTransition(observed, expected, error_msg,
                      variable_descriptions={})
                    </code>{' '}
                    - {t("indicates the student's code failed the test.")}
                    <ul>
                      <li>
                        <code>observed</code> - {t('the actual output seen')}
                      </li>
                      <li>
                        <code>expected</code> -{' '}
                        {t(
                          "what output was expected. For cases where we can't determine the exact output using a failed regex, we can use ",
                        )}
                        <code>{'#[<var_name>]'}</code>{' '}
                        {t('to indicate a non-deterministic output.')}
                      </li>
                      <li>
                        <code>error_msg</code> -{' '}
                        {t('message to display to student')}
                      </li>
                      <li>
                        <code>variable_descriptions</code> -{' '}
                        {t(
                          'optional dict mapping variable names to descriptions for template substitutions ',
                        )}
                        <code>{'#[<var_name>]'}</code>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <code>
                      FailedTransitionWithInput(observed_output, example_input,
                      error_msg)
                    </code>{' '}
                    -{' '}
                    {t(
                      "indicates the student's code printed an output but we expected user input.",
                    )}
                    <ul>
                      <li>
                        <code>observed_output</code> -{' '}
                        {t('the actual output seen')}
                      </li>
                      <li>
                        <code>example_input</code> -{' '}
                        {t(
                          'example of valid input, used to show where we expected input',
                        )}
                      </li>
                      <li>
                        <code>error_msg</code> -{' '}
                        {t('message to display to student')}
                      </li>
                    </ul>
                  </li>
                  <li>
                    <code>SuccessfulInputTransition(dynamic_input)</code> -{' '}
                    {t('indicates valid input was received.')}
                    <ul>
                      <li>
                        <code>dynamic_input</code> -{' '}
                        {t("the input string to provide to the student's code")}
                      </li>
                    </ul>
                  </li>
                  <li>
                    <code>FailedInputTransition(error_msg)</code> -{' '}
                    {t('indicates invalid input was received.')}
                    <ul>
                      <li>
                        <code>error_msg</code> -{' '}
                        {t('message to display to student')}
                      </li>
                    </ul>
                  </li>
                  <li>
                    <code>
                      FailedInputTransitionWithOutput(error_msg, example_input)
                    </code>{' '}
                    -{' '}
                    {t(
                      'indicates input was received when output was expected.',
                    )}
                    <ul>
                      <li>
                        <code>error_msg</code> -{' '}
                        {t('message to display to student')}
                      </li>
                      <li>
                        <code>example_input</code> -{' '}
                        {t(
                          "example of valid input, used to display where input should've been",
                        )}
                      </li>
                    </ul>
                  </li>
                  <li>
                    <code>SuccessfulFinishState()</code> -{' '}
                    {t('indicates program finished successfully.')}
                  </li>
                  <li>
                    <code>FailedFinishState(error_msg)</code> -{' '}
                    {t('indicates program finished unsuccessfully.')}
                    <ul>
                      <li>
                        <code>error_msg</code> -{' '}
                        {t('message to display to student')}
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        {branches.map((b, i) => (
          <AdvancedConsoleCustomScript
            // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
            key={i}
            branch={b}
            editAdvancedBranch={newBranch => editAdvancedBranch(i, newBranch)}
            deleteAdvancedBranch={() => deleteAdvancedBranch(i)}
            isEditable={isEditable}
          />
        ))}
        <button
          className="btn btn-secondary my-2 mr-2"
          onClick={addAdvancedBranch}
          disabled={!isEditable}
        >
          Add New Test
        </button>
      </div>
    </div>
  );
};

interface AdvancedConsoleCustomScriptProps {
  branch: AdvancedConsoleUnitTest;
  editAdvancedBranch: (branch: AdvancedConsoleUnitTest) => void;
  deleteAdvancedBranch: () => void;
  isEditable: boolean;
}

export const AdvancedConsoleCustomScript = ({
  branch,
  editAdvancedBranch,
  deleteAdvancedBranch,
  isEditable,
}: AdvancedConsoleCustomScriptProps) => {
  const editName = name => {
    editAdvancedBranch({
      ...branch,
      name,
    });
  };

  const updateCode = (newCode, script) => {
    editAdvancedBranch({
      ...branch,
      gradingScripts: {
        ...branch.gradingScripts,
        [script]: newCode,
      },
    });
  };

  return (
    <div className="border rounded p-2 mb-3 gap-2">
      <div className="d-flex flex-row justify-content-between align-items-center">
        <div className="form-group d-flex flex-row align-items-center">
          <h3 className="mr-2">Name:</h3>
          <input
            type="text"
            className="form-control"
            value={branch.name}
            onChange={e => editName(e.target.value)}
            disabled={!isEditable}
          />
        </div>
        <button
          className="btn btn-danger"
          style={{height: 'fit-content'}}
          onClick={() => deleteAdvancedBranch()}
          disabled={!isEditable}
        >
          X
        </button>
      </div>
      <b className="mt-2">onInput Script</b>
      <div>
        <ExpandableCodeEditor
          mode="python"
          value={branch.gradingScripts.onInput}
          onChange={e => updateCode(e, 'onInput')}
          readOnly={!isEditable}
        />
      </div>
      <b className="mt-2">onOutput Script</b>
      <OutputOuter>
        <ExpandableCodeEditor
          mode="python"
          value={branch.gradingScripts.onOutput}
          onChange={e => updateCode(e, 'onOutput')}
          readOnly={!isEditable}
        />
      </OutputOuter>
      <b className="mt-2">onFinish Script</b>
      <OutputOuter>
        <ExpandableCodeEditor
          mode="python"
          value={branch.gradingScripts.onFinish}
          onChange={e => updateCode(e, 'onFinish')}
          readOnly={!isEditable}
        />
      </OutputOuter>
    </div>
  );
};

const OutputOuter = styled.div``;
